import { Breadcrumbs, Link, Typography, colors } from '@mui/material'
import { FunctionComponent, PropsWithChildren } from 'react'
import { kebabCaseToPascalCase } from '@utils'
import { Link as WouterLink, useLocation } from 'wouter'

type Props = {
    href: string
}

const Breadcrumb: FunctionComponent<PropsWithChildren<Props>> = (props) => {
    return (
        <Link
            underline="hover"
            href={props.href}
            component={WouterLink}
            color={colors.grey[700]}
        >
            {props.children}
        </Link>
    )
}

type BreadcrumbsProps = {
    /**
     * What to call the page you are on, in place of the last URL segment. Detail pages
     * are addressed by id, and "Podium 0762849d5fbde72c" is not what anyone calls the
     * device. Falls back to the segment when a page doesn't set it (or hasn't loaded
     * yet).
     */
    currentLabel?: string | null
}

const DashboardBreadcrumbs = ({ currentLabel }: BreadcrumbsProps) => {
    const [location] = useLocation()

    let pathSegments: string[] = []

    // no crumb needed for the root path
    if (location !== '/') {
        // chop off the leading "/" and then split the path segments
        pathSegments = location.slice(1).split('/')
    }

    return (
        <div style={{ paddingBlock: 8 }}>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
                <Breadcrumb href="/">Home</Breadcrumb>
                {pathSegments.map((segment, index) => {
                    // The last segment is the page you are already on, and on a detail
                    // page it is a record id, so `/<id>` is not a route at all — linking
                    // it only offers a 404.
                    if (index === pathSegments.length - 1) {
                        return (
                            <Typography
                                key={`${index}-${segment}`}
                                color={colors.grey[700]}
                            >
                                {currentLabel || kebabCaseToPascalCase(segment)}
                            </Typography>
                        )
                    }

                    return (
                        <Breadcrumb
                            key={`${index}-${segment}`}
                            href={`/${segment}`}
                        >
                            {kebabCaseToPascalCase(segment)}
                        </Breadcrumb>
                    )
                })}
            </Breadcrumbs>
        </div>
    )
}

export default DashboardBreadcrumbs
